import React from 'react'
import ReactSelect from 'react-select'
import ReactCreatableSelect from 'react-select/creatable'
import { FormProps } from 'utils-next/forms'

import { WithStyles, withStyles, WithTheme } from '@mui/styles'

import { SelectOption } from '../Common/Select.model'
import { selectComponents } from '../Common/Select.partials'
import styles from '../Common/Select.styles'

class Select extends React.PureComponent<SelectProps & WithStyles<typeof styles> & WithTheme> {
  constructor(props: SelectProps & WithStyles<typeof styles> & WithTheme) {
    super(props)

    this.handleOnCreateOption = this.handleOnCreateOption.bind(this)
    this.inputProps = this.inputProps.bind(this)
    this.onChange = this.onChange.bind(this)
    this.getErrorAndHelperText = this.getErrorAndHelperText.bind(this)
  }

  async handleOnCreateOption(inputValue: string) {
    const { value, onCreateOption, isMulti } = this.props
    if (!onCreateOption) {
      return
    }

    const newOption = onCreateOption(inputValue)
    if (newOption) {
      const newValue = isMulti ? ((value as any[]) || []).concat([newOption]) : newOption
      this.onChange(newValue)
    }
  }

  onChange(value: SelectOption | Array<SelectOption> | null) {
    const { field, form, onChange } = this.props
    let parsedValue: SelectOption | Array<SelectOption> | '' = ''
    if (value && value.constructor === Array) {
      parsedValue = (value as Array<SelectOption>).length > 0 ? value : ''
    } else {
      parsedValue = value || ''
    }
    if (!!onChange) {
      onChange(parsedValue)
    } else if (!!field && !!form) {
      const { setFieldValue } = form
      setFieldValue(field.name, parsedValue)
    }
  }

  inputProps() {
    const {
      onCreateOption,
      disabled = false,
      isMulti = false,
      options,
      field,
      isClearable = true,
      ...otherProps
    } = this.props

    return {
      ...field,
      ...otherProps,
      options,
      onChange: this.onChange,
      components: selectComponents(false),
      isMulti,
      isDisabled: disabled,
      isClearable,
      defaultOptions: true,
      onCreateOption: !!onCreateOption ? this.handleOnCreateOption : undefined,
      menuPortalTarget: document.body,
    }
  }

  getErrorAndHelperText() {
    const { error, helperText, field, form } = this.props
    if (!!field && !!form) {
      const errorMessage = form.errors[field.name]
      return {
        error: !!errorMessage,
        helperText: !!errorMessage ? errorMessage : helperText,
      }
    } else {
      return {
        error,
        helperText,
      }
    }
  }

  render() {
    const { classes, theme, label, placeholder, disabled = false, required, onCreateOption } = this.props

    const selectStyles = {
      input: (base: React.CSSProperties) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
      menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 9999 }),
    }

    const styleProps = {
      classes,
      textFieldProps: {
        label,
        InputLabelProps: {
          shrink: true,
          required,
        },
        placeholder,
        disabled,
        variant: 'standard',
        ...this.getErrorAndHelperText(),
      },
    }

    const SelectComponent: any = !!onCreateOption ? ReactCreatableSelect : ReactSelect

    return <SelectComponent styles={selectStyles} {...this.inputProps()} {...styleProps} />
  }
}

export type SelectProps = {
  field?: {
    onChange: (e: React.ChangeEvent<any>) => void
    onBlur: (e: any) => void
    value: any
    name: string
  }
  className?: string
  form?: FormProps<any>
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  isMulti?: boolean
  isLoading?: boolean
  isClearable?: boolean
  autoFocus?: boolean
  value?: any | Array<any> | null
  required?: boolean
  filterOption?: (input: any) => boolean
  error?: boolean
  helperText?: string
  options?: Array<any>
  onChange?: (value?: any | Array<any> | null) => void
  onBlur?: () => void
  onCreateOption?: (inputValue: string) => any
  isValidNewOption?: (args: any) => boolean
  getOptionValue?: (option: any) => string
  getOptionLabel?: (option: any) => string
  noOptionsMessage?: (option: any) => string
  formatOptionLabel?: (option: any, extraData: any) => React.ReactNode
}

export default withStyles(styles, { withTheme: true })(Select)
